import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider, useAuth} from '@apps-common/auth-react';
import {DocumentHandler} from '@apps-common/auth-utils';
import {setAuthToken} from '@apps-common/js-client/api/request';
import NavBar from '@apps-common/ui-components/NavBar';
import {CssBaseline} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, {useCallback} from 'react';
import {createRoot} from 'react-dom/client';
import {BrowserRouter, useHistory} from 'react-router-dom';
import Routes from './routes.jsx';
import {ThemeProvider} from './theme-provider.jsx';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('UTC');

function TopBar() {
  const auth = useAuth();

  return (
    <ThemeProvider dark>
      <NavBar
        title="Analytic Feeds"
        user={auth.user?.claims}
        logout={() => {
          auth.signOut();
        }}
      />
    </ThemeProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const authClient = new AuthClient({
  clientId:
    import.meta.env.VITE_OIDC_CLIENT_ID || 'm3GGDeESkl2H9uOABxkIMIIhFahJqPc2',
  redirectUri: `${window.location.origin}${import.meta.env.BASE_URL}`,
});

function App() {
  const history = useHistory();
  const navigate = useCallback(url => history.replace(url), [history]);

  const onAuthChange = useCallback(state => {
    setAuthToken(state.user?.accessToken || '');
  }, []);

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      private
      onChange={onAuthChange}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <CssBaseline />
            <DocumentHandler>
              <TopBar />
              <Routes />
            </DocumentHandler>
          </ThemeProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </AuthProvider>
  );
}

const root = createRoot(document.getElementById('root'));
const basename = import.meta.env.BASE_URL || '';

root.render(
  <BrowserRouter basename={basename}>
    <App />
  </BrowserRouter>,
);
